import { startTransition, useEffect, useMemo, useState } from "react"

import { Skeleton } from "@/components/ui/skeleton"
import {
  apiClient,
  type IncidentExportRecord,
  type OperatorOrganizationRecord,
} from "@/lib/api/client"
import {
  formatDateTime,
  formatRelativeTime,
  getPartnerSummary,
  INPUT_CLASS,
  matchesFilterText,
  safeJsonStringify,
  shortId,
  titleizeToken,
} from "@/features/operator/advanced/advanced-ops-utils"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  canManagePortalOrganizations,
  resolveOperatorSession,
  type OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"

export interface ExportsSearch {
  exportId?: string
  exportType?: string
  organizationId?: string
  q?: string
}

interface ExportsWorkspaceProps {
  onSearchChange: (next: Partial<ExportsSearch>) => void
  search: ExportsSearch
}

interface ExportsBundle {
  exports: IncidentExportRecord[]
  organizations: OperatorOrganizationRecord[]
  session: OperatorResolvedSession
}

function canAccessExportsWorkspace(session: OperatorResolvedSession) {
  return Boolean(session.permissions.manageExports || session.permissions.exportEvidence)
}

async function loadExportsBundle(search: ExportsSearch): Promise<ExportsBundle> {
  const session = await resolveOperatorSession()

  if (!canAccessExportsWorkspace(session)) {
    throw new Error("This session does not have access to the exports workspace.")
  }

  const organizationId = canManagePortalOrganizations(session)
    ? search.organizationId
    : undefined

  const [exportsResult, organizationsResult] = await Promise.all([
    apiClient.getExportPackages({
      includeManifest: true,
      limit: 200,
      organizationId,
    }),
    canManagePortalOrganizations(session)
      ? apiClient.getOrganizations()
      : Promise.resolve(null),
  ])

  return {
    exports: exportsResult.data ?? [],
    organizations: organizationsResult?.data ?? [],
    session,
  }
}

export function ExportsWorkspace({
  onSearchChange,
  search,
}: ExportsWorkspaceProps) {
  const [bundle, setBundle] = useState<ExportsBundle | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isActive = true

    async function load() {
      startTransition(() => {
        setIsLoading(true)
        setErrorMessage(null)
      })

      try {
        const nextBundle = await loadExportsBundle(search)

        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(nextBundle)
          setIsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(null)
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to load the exports workspace."
          )
          setIsLoading(false)
        })
      }
    }

    void load()

    return () => {
      isActive = false
    }
  }, [search.organizationId])

  const filteredExports = useMemo(() => {
    if (!bundle) {
      return []
    }

    return bundle.exports.filter((record) => {
      if (search.exportType && search.exportType !== "all" && record.exportType !== search.exportType) {
        return false
      }

      return matchesFilterText(search.q, [
        record.id,
        record.exportType,
        record.recipientKey,
        record.governance?.purposeLabel,
        record.governance?.regionKey,
        record.governance?.entitlementKey,
      ])
    })
  }, [bundle, search.exportType, search.q])

  const selectedExport =
    filteredExports.find((record) => record.id === search.exportId) ?? filteredExports[0] ?? null
  const portalScoped = Boolean(bundle && canManagePortalOrganizations(bundle.session))
  const deliveredExports = filteredExports.filter(
    (record) => getPartnerSummary(record).latestStatus === "delivered"
  ).length
  const liveExports = filteredExports.filter(
    (record) => (getPartnerSummary(record).liveCount ?? 0) > 0
  ).length

  return (
    <>
      {errorMessage ? (
        <OperatorStatusMessage tone="danger">{errorMessage}</OperatorStatusMessage>
      ) : null}

      {isLoading ? <ExportsWorkspaceLoadingState /> : null}

      {!isLoading && bundle ? (
        <>
          <OperatorPanel
            title="Filters and scope"
            description="Filter export visibility by organization, export type, and search text."
          >
            <div className="grid gap-4 lg:grid-cols-4">
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Organization scope</span>
                <select
                  aria-label="Organization scope"
                  className={INPUT_CLASS}
                  disabled={!portalScoped}
                  value={search.organizationId ?? ""}
                  onChange={(event) =>
                    onSearchChange({
                      organizationId: event.target.value || undefined,
                    })
                  }
                >
                  <option value="">All visible organizations</option>
                  {bundle.organizations.map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="font-medium">Export type</span>
                <select
                  aria-label="Export type"
                  className={INPUT_CLASS}
                  value={search.exportType ?? "all"}
                  onChange={(event) =>
                    onSearchChange({
                      exportType:
                        event.target.value === "all" ? undefined : event.target.value,
                    })
                  }
                >
                  <option value="all">All export types</option>
                  <option value="evidence">Evidence</option>
                  <option value="insurance_fnol">Insurance FNOL</option>
                  <option value="government_share">Government share</option>
                  <option value="institutional_summary">Institutional summary</option>
                </select>
              </label>
              <label className="grid gap-2 text-sm lg:col-span-2">
                <span className="font-medium">Search</span>
                <input
                  aria-label="Search export packages"
                  className={INPUT_CLASS}
                  value={search.q ?? ""}
                  placeholder="Export, recipient, governance, region, entitlement"
                  onChange={(event) =>
                    onSearchChange({
                      q: event.target.value || undefined,
                    })
                  }
                />
              </label>
            </div>
          </OperatorPanel>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <OperatorMetricCard
              detail={`${filteredExports.filter((record) => record.exportType === "insurance_fnol").length} FNOL packages`}
              label="Exports in scope"
              value={filteredExports.length}
            />
            <OperatorMetricCard
              detail={`${liveExports} packages already have live-visible delivery chains`}
              label="Live-linked exports"
              value={liveExports}
            />
            <OperatorMetricCard
              detail={`${deliveredExports} packages have delivered latest status`}
              label="Delivered posture"
              value={deliveredExports}
            />
            <OperatorMetricCard
              detail={`Most recent package ${
                filteredExports[0]?.createdAt
                  ? formatDateTime(filteredExports[0].createdAt)
                  : "not recorded"
              }`}
              label="Latest checksum"
              value={shortId(filteredExports[0]?.checksum, 12)}
            />
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <OperatorPanel
              title="Export packages"
              description="These records are immutable once prepared. Delivery chains and callbacks attach downstream without mutating manifest content."
            >
              {filteredExports.length ? (
                <div className="grid gap-3">
                  {filteredExports.map((record) => {
                    const summary = getPartnerSummary(record)
                    const isActive = record.id === selectedExport?.id

                    return (
                      <button
                        key={record.id}
                        className={`rounded-3xl border px-4 py-4 text-left transition-colors ${
                          isActive
                            ? "border-primary/55 bg-primary/8"
                            : "border-border/70 bg-background/65 hover:border-primary/35"
                        }`}
                        type="button"
                        onClick={() => onSearchChange({ exportId: record.id })}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold">
                              {titleizeToken(record.exportType)} · {shortId(record.id)}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {record.recipientType || "recipient"} ·{" "}
                              {record.recipientKey || "unassigned"}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                              {titleizeToken(record.status)}
                            </span>
                            <span className="rounded-full bg-background px-3 py-1">
                              {titleizeToken(summary.latestStatus)}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                          <div>Purpose {record.governance?.purposeLabel || "n/a"}</div>
                          <div>Region {record.governance?.regionKey || "n/a"}</div>
                          <div>Chains {summary.totalDeliveries ?? 0}</div>
                          <div>Latest activity {formatRelativeTime(summary.latestActivityAt)}</div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              ) : (
                <OperatorEmptyState
                  title="No export packages match the current filters."
                  message="Adjust the scope or search parameters to inspect another export package."
                />
              )}
            </OperatorPanel>

            <OperatorPanel
              title="Selected export detail"
              description="Review governance details, signatures, and manifest content for the selected export."
            >
              {selectedExport ? (
                <div className="grid gap-4">
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold">
                          {titleizeToken(selectedExport.exportType)} · {shortId(selectedExport.id)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Prepared {formatDateTime(selectedExport.createdAt)}
                        </div>
                      </div>
                      <div className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
                        {titleizeToken(selectedExport.status)}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                      <div>Checksum {selectedExport.checksum || "Pending"}</div>
                      <div>Export version {selectedExport.contractVersion || "Unknown"}</div>
                      <div>Purpose {selectedExport.governance?.purposeLabel || "n/a"}</div>
                      <div>Entitlement {selectedExport.governance?.entitlementKey || "n/a"}</div>
                      <div>Region {selectedExport.governance?.regionKey || "n/a"}</div>
                      <div>
                        Partner summary {titleizeToken(getPartnerSummary(selectedExport).latestStatus)}
                      </div>
                    </div>
                  </div>

                  {selectedExport.manifest ? (
                    <details className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <summary className="cursor-pointer text-sm font-semibold">
                        Manifest preview
                      </summary>
                      <pre className="mt-3 max-h-96 overflow-auto rounded-2xl bg-background p-3 text-xs">
                        {safeJsonStringify(selectedExport.manifest)}
                      </pre>
                    </details>
                  ) : (
                    <OperatorEmptyState
                      title="Manifest content not exposed."
                      message="Manifest content is not available for this export package."
                    />
                  )}
                </div>
              ) : (
                <OperatorEmptyState
                  title="No export selected."
                  message="Choose an export package to inspect governance posture and manifest details."
                />
              )}
            </OperatorPanel>
          </div>
        </>
      ) : null}
    </>
  )
}

function ExportsWorkspaceLoadingState() {
  return (
    <>
      <OperatorPanel
        title="Filters and scope"
        description="Filter export visibility by organization, export type, and search text."
      >
        <div className="grid gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={`exports-filter-${index}`}
              className="h-11 w-full rounded-2xl"
            />
          ))}
        </div>
      </OperatorPanel>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={`exports-metric-${index}`}
            className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
          >
            <Skeleton className="h-4 w-28" />
            <Skeleton className="mt-3 h-8 w-20" />
            <Skeleton className="mt-3 h-4 w-full" />
          </div>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <OperatorPanel
          title="Export packages"
          description="These records are immutable once prepared. Delivery chains and callbacks attach downstream without mutating manifest content."
        >
          <div className="grid gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`exports-package-${index}`}
                className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="mt-2 h-4 w-28" />
                  </div>
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-24 rounded-full" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, detailIndex) => (
                    <Skeleton
                      key={`exports-package-detail-${index}-${detailIndex}`}
                      className="h-4 w-full"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </OperatorPanel>

        <OperatorPanel
          title="Selected export detail"
          description="Review governance details, signatures, and manifest content for the selected export."
        >
          <div className="grid gap-4">
            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="mt-3 h-4 w-full" />
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton
                    key={`exports-detail-${index}`}
                    className="h-4 w-full"
                  />
                ))}
              </div>
            </div>
            <Skeleton className="h-48 w-full rounded-3xl" />
          </div>
        </OperatorPanel>
      </div>
    </>
  )
}
